import React from 'react';
import { Box, Stack, Text, Button, Field, Input, CloseButton, Alert, Dialog, Portal } from '@chakra-ui/react';
import { InputGroup } from '@/components/ui';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { signupStyles } from '../../signup-templates/signup.styles';
import { PaymentProps } from '../../templates';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { paymentSchema, TPayment } from './payment.validator';
import { useAuthState } from '@/app/_providers/store';
import { createPaymentIntent, updateWithZeroPayment } from '@/services/payment/payment.service';
import BarLoader from '@/components/ui/loaders/BarLoader';
import Image from 'next/image';
import useProductPrice from '@/hooks/product/useProductPrice';
import { getMe } from '@/services/user/user.service';
import useAuthFunc from '@/hooks/auth/useAuthFunc';
import { discountCouponRedemption } from '@/services/discount/discount.service';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';

const PaymentForm = ({ previous }: { previous: PaymentProps['previous'] }) => {
   const [discounted, setDiscounted] = React.useState(false);
   const { refreshSession, redirect } = useAuthFunc();
   const { product, price, setPrice } = useProductPrice();
   const [paymentSuccess, setPaymentSuccess] = React.useState(false);
   const { user } = useAuthState();
   const stripe = useStripe();
   const elements = useElements();
   const [cardReady, setCardReady] = React.useState(false);
   const [cardComplete, setCardComplete] = React.useState(false);
   const [cardError, setCardError] = React.useState<string | null>(null);
   const paymentIntentRef = React.useRef<{ clientSecret: string; amount: number } | null>(null);

   const handleChange = (event: StripeCardElementChangeEvent) => {
      setCardComplete(event.complete);
      setCardError(event.error ? event.error.message : null);
   };

   const {
      register,
      handleSubmit,
      setValue,
      watch,
      setError,
      formState: { errors, isSubmitting },
   } = useForm<TPayment>({ defaultValues: { auto_renew: false }, resolver: zodResolver(paymentSchema) });

   const [discountValue, setDiscountValue] = React.useState(0);

   const { discount_code } = watch();

   const onSubmit = handleSubmit(async values => {
      if (!elements || !stripe || !product || !cardReady) {
         return;
      }

      if ((price as number) < 0) {
         return;
      }

      if (!cardComplete) {
         setCardError('Card details are incomplete');
         return;
      }

      // if price zero call api
      if (price === 0) {
         const [error] = await updateWithZeroPayment(user?._id as string, {
            couponCode: discount_code as string,
            reason: 'new',
         });
         if (error) {
            // Show error to your customer
            setError('root', { message: error?.message || 'Failed to submit payment' });
            return;
         }
         await refreshSession();
         redirect('KYC');
         setPaymentSuccess(true);
         return;
      }

      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
         setError('root', { message: 'Card input not ready.', type: 'manual' });
         return;
      }

      let clientSecret = '';

      if (paymentIntentRef.current && paymentIntentRef.current.amount === price) {
         clientSecret = paymentIntentRef.current.clientSecret;
      } else {
         // Create the PaymentIntent and obtain clientSecret from your server endpoint
         const [error, data] = await createPaymentIntent({
            email: user?.email as string,
            name: `${user?.first_name} ${user?.last_name}`,
            method: 'card',
            amount: (product.priceDetails.amount as number) / 100,
            currency: 'gbp',
            auto_renew: values.auto_renew,
            couponCode: values.discount_code,
            reason: 'new',
         });
         clientSecret = data?.response.clientSecret as string;
         paymentIntentRef.current = {
            clientSecret: data?.response.clientSecret as string,
            amount: price as number,
         };

         if (error) {
            console.log(error);
            setError('root', { message: error.response?.data.message as string, type: 'manual' });
            return;
         }
      }

      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
         type: 'card',
         card: cardElement,
         billing_details: {
            email: user?.email as string,
            name: `${user?.first_name} ${user?.last_name}`,
         },
      });

      if (paymentMethodError) {
         setError('root', { message: 'Payment not created.', type: 'manual' });
         return;
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
         payment_method: paymentMethod.id,
      });

      if (stripeError) {
         console.log(stripeError);
         setError('root', { message: stripeError.message as string, type: 'manual' });
         return;
      }

      if (paymentIntent.status === 'succeeded') {
         setPaymentSuccess(true);
      }

      // if (values.auto_renew) {
      //    await startAutoRenewal({
      //       paymentMethod: paymentIntent.payment_method as string,
      //       priceId: product.defaultPrice as string,
      //    });
      // }

      const interval = setInterval(async () => {
         const [error, data] = await getMe();

         if (error) {
            console.log(error);
         }

         if (data?.response?.registration_step === 'KYC') {
            await refreshSession();
            redirect(data?.response?.registration_step);
            clearInterval(interval);
         }
      }, 2000);
   });

   const onCouponRedemption = async () => {
      if (!discount_code || errors.discount_code) return;

      const [error, data] = await discountCouponRedemption(discount_code as string);

      if (error) {
         console.log(error);
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         return;
      }

      if (data.response.amount_type === 'percentage') {
         const discountValue = (price as number) * (data.response.amount / 100);
         setDiscountValue(parseFloat(discountValue.toFixed(2)));
         setPrice(parseFloat(((price as number) - discountValue).toFixed(2)));
      }

      if (data.response.amount_type === 'fixed') {
         setDiscountValue(parseFloat(data.response.amount.toFixed(2)));
         setPrice(parseFloat(((price as number) - data.response.amount).toFixed(2)));
      }

      setDiscounted(true);
   };

   const endElement = discounted ? (
      <CloseButton
         size="md"
         color="brand"
         bg="transparent"
         onClick={() => {
            setValue('discount_code', '');
            setDiscounted(false);
            setPrice((product?.priceDetails.amount as number) / 100 || 29);
            setDiscountValue(0);
         }}
         me="-2"
      />
   ) : (
      <Button
         width="70px"
         height="30px"
         fontSize={'xs'}
         fontWeight="semibold"
         onClick={onCouponRedemption}
         disabled={!discount_code}
      >
         Apply
      </Button>
   );

   return (
      <Box as="form" css={signupStyles.signup_form} onSubmit={onSubmit}>
         {isSubmitting && <BarLoader loadingText="Processing payment..." />}
         <Stack gap="4" align="flex-start" minW="full">
            {errors.root?.message && (
               <Alert.Root status="error">
                  <Alert.Indicator />
                  <Alert.Title>{errors.root.message}</Alert.Title>
               </Alert.Root>
            )}
            {cardError && (
               <Alert.Root status="error">
                  <Alert.Indicator />
                  <Alert.Title>{cardError}</Alert.Title>
               </Alert.Root>
            )}
            <Field.Root>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Discount code
               </Field.Label>
               <InputGroup endElement={endElement} style={{ width: '100%' }}>
                  <Input
                     placeholder="Discount Code"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('discount_code')}
                  />
               </InputGroup>
            </Field.Root>
            <Box css={signupStyles.payment_details}>
               <Box css={signupStyles.payment_details_con}>
                  <Text textStyle="xs" color="white">
                     Cancard verification and the card
                  </Text>
                  <Text textStyle="xs" color="white">
                     {/* £{price || 29} */}£{(product?.priceDetails?.amount as number) / 100}
                  </Text>
               </Box>
               <Box css={signupStyles.payment_details_con}>
                  <Text textStyle="xs" color="white">
                     Discount
                  </Text>
                  <Text textStyle="xs" color="white">
                     {/* -£0.00  */}£{`${discountValue}`}
                  </Text>
               </Box>
               <Box css={signupStyles.payment_details_total}>
                  <Text textStyle="xs" color="white">
                     Total Amount
                  </Text>
                  <Text textStyle="xs" color="white">
                     £{price}
                  </Text>
               </Box>
            </Box>
            <Field.Root>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Card number
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>

               {/* Move CardElement inside this container */}
               <Box css={signupStyles.card_details}>
                  <CardElement
                     onReady={() => setCardReady(true)}
                     onChange={handleChange}
                     options={{
                        style: {
                           base: {
                              fontSize: '16px',
                              color: '#fff',
                              '::placeholder': {
                                 color: '#bbb',
                              },
                           },
                           invalid: {
                              color: '#e5424d',
                           },
                        },
                     }}
                  />
               </Box>
            </Field.Root>

            {/* <Box css={signupStyles.auto_renewal}>
               <Box>
                  <Text textStyle="xs" color="white" fontWeight="bold">
                     Auto-Renewal
                  </Text>
                  <Text textStyle="xs" color="white">
                     Pay the next renewal automatically
                  </Text>
               </Box>
               <Box>
                  <Switch.Root
                     name="auto_renew"
                     checked={auto_renew}
                     onCheckedChange={({ checked }) => {
                        setValue('auto_renew', checked);
                     }}
                  >
                     <Switch.Label textStyle="xs" color="white" w="26px">
                        {auto_renew ? 'ON' : 'OFF'}
                     </Switch.Label>
                     <Switch.HiddenInput />
                     <Switch.Control bg={auto_renew ? 'brand' : 'darkGrey'} color={auto_renew ? 'white' : 'white'} />
                  </Switch.Root>
               </Box>
            </Box> */}
            <Box css={signupStyles.signup_btn_con}>
               <Button onClick={previous} bg="darkerBtn" textStyle="xs" color="white" h="10" maxW="36" minW="36" p="0">
                  Back
               </Button>
               <Button
                  type="submit"
                  disabled={isSubmitting || !cardReady}
                  textStyle="xs"
                  h="10"
                  maxW="36"
                  minW="36"
                  p="0"
               >
                  Pay Now
               </Button>
            </Box>
         </Stack>
         <Dialog.Root open={paymentSuccess} lazyMount placement="center" motionPreset="slide-in-bottom">
            <Portal>
               <Dialog.Backdrop />
               <Dialog.Positioner>
                  <Dialog.Content bg="#202020" rounded="15px" py="4" maxWidth="340px">
                     <Dialog.Body>
                        <Box
                           w="16"
                           h="16"
                           rounded="50%"
                           bg="#f5e87614"
                           m="0 auto 10px"
                           display="flex"
                           alignItems="center"
                           justifyContent="center"
                        >
                           <Image src="/assets/gif/payment_success.gif" alt="" width="30" height="30" />
                        </Box>
                        <Dialog.Title color="white" textStyle="xl" textAlign="center" mb="2">
                           Payment successfull
                        </Dialog.Title>
                        <Text color="darkGray" textStyle="sm" textAlign="center">
                           We’ve sent you an email with the necessary instructions to continue
                        </Text>
                     </Dialog.Body>
                     <Dialog.Footer justifyContent="center" pt="0">
                        <Dialog.ActionTrigger asChild>
                           <Button variant="outline" h="10" onClick={() => {}}>
                              OK
                           </Button>
                        </Dialog.ActionTrigger>
                     </Dialog.Footer>
                  </Dialog.Content>
               </Dialog.Positioner>
            </Portal>
         </Dialog.Root>
      </Box>
   );
};

export default PaymentForm;
